import { Channel, EmailAction } from '@/models';
import { EmailRateMeasures } from '@/api/queries/interaction/useEmailRateMeasures';
import { toDate, toNumberOrNull, toStringOrNull } from '@/utils/cube';
import { CubeRawRow } from '@/utils/cube/mapper.type';
import { HCP_CUBE_NAME } from '@api/hcp';
import { DeeplyReadonly, Filter, LogicalAndFilter } from '@cubejs-client/core';
import { Dayjs } from 'dayjs';
import {
  ChannelConfigMap,
  CUBE_AGGREGATE_INTERACTION,
  CUBE_NAME,
  CUBE_WEBSITE_AGGREGATE_INTERACTION,
  InteractionCoverage,
} from './types';

export const getChannelFilter = (channel: Channel): DeeplyReadonly<Filter> => {
  const channelFilters: { [key in Channel]?: DeeplyReadonly<Filter> } = {
    [Channel.VISITS_1_TO_1]: {
      and: [
        {
          member: `interaction_aggregate.is_1to1visit`,
          operator: 'set',
        },
      ],
    },
    [Channel.EMAIL]: {
      and: [{ member: `${CUBE_AGGREGATE_INTERACTION}.channel`, operator: 'equals', values: ['Email'] }],
    },
    [Channel.EVENT]: {
      member: `${CUBE_AGGREGATE_INTERACTION}.hcp_bk`,
      operator: 'set',
    },
    [Channel.WEBSITE]: {
      member: `${CUBE_AGGREGATE_INTERACTION}.is_website_visit`,
      operator: 'equals',
      values: ['true'],
    },
  };

  const emptyFilter: DeeplyReadonly<Filter> = { and: [] };
  const filter = channelFilters[channel];
  return filter || emptyFilter;
};

export const getEmailFilter = (action: Exclude<EmailAction, EmailAction.DELIVERED>): DeeplyReadonly<Filter> => {
  const emailFilters: { [key in Exclude<EmailAction, EmailAction.DELIVERED>]: DeeplyReadonly<Filter> } = {
    [EmailAction.SENT]: { member: `${CUBE_NAME}.date_send`, operator: 'set' },
    [EmailAction.OPENED]: { member: `${CUBE_AGGREGATE_INTERACTION}.email_open_date`, operator: 'set' },
    [EmailAction.CLICKED]: { member: `${CUBE_AGGREGATE_INTERACTION}.email_clicked_date`, operator: 'set' },
    [EmailAction.BOUNCED]: { member: `${CUBE_AGGREGATE_INTERACTION}.email_bounce_date`, operator: 'set' },
  };

  const emptyFilter: DeeplyReadonly<Filter> = { member: '', operator: 'equals', values: [] };
  const filter = emailFilters[action];

  return filter || emptyFilter;
};

export const getCoverageMeasuresByChannel = (channel: Channel, emailRateMeasures: EmailRateMeasures): string[] => {
  const measuresMap: { [key in Channel]?: string[] } = {
    [Channel.EMAIL]: [emailRateMeasures.clickRate, emailRateMeasures.openRate],
    [Channel.VISITS_1_TO_1]: [`${CUBE_AGGREGATE_INTERACTION}.countCompletedFaceToFaceDistinctByHCP`],
    [Channel.WEBSITE]: [`${CUBE_AGGREGATE_INTERACTION}.countWebsiteDistinctByHCP`],
    [Channel.EVENT]: [`${CUBE_AGGREGATE_INTERACTION}.numberOfUniqueParticipants`],
  };

  return measuresMap[channel] || [];
};

export const extractCoverageResultsFromRow =
  (channel: Channel, hcpCount: number, emailRateMeasures: EmailRateMeasures) =>
  (row: CubeRawRow): InteractionCoverage => {
    switch (channel) {
      case Channel.EMAIL:
        return {
          month: toDate(row[`${CUBE_AGGREGATE_INTERACTION}.interaction_date.month`]),
          rate1: toNumberOrNull(row[emailRateMeasures.openRate]) ?? 0,
          rate2: toNumberOrNull(row[emailRateMeasures.clickRate]) ?? 0,
          type: toStringOrNull(row[`${CUBE_AGGREGATE_INTERACTION}.activity_type`], ''),
        };
      case Channel.VISITS_1_TO_1: {
        return {
          month: toDate(row[`${CUBE_AGGREGATE_INTERACTION}.interaction_date.month`]),
          rate1:
            (toNumberOrNull(row[`${CUBE_AGGREGATE_INTERACTION}.countCompletedFaceToFaceDistinctByHCP`]) ?? 0) /
            hcpCount,
          type: toStringOrNull(row[`${CUBE_AGGREGATE_INTERACTION}.activity_type`], ''),
        };
      }
      case Channel.WEBSITE:
        return {
          month: toDate(row[`${CUBE_AGGREGATE_INTERACTION}.interaction_date.month`]),
          rate1: (toNumberOrNull(row[`${CUBE_AGGREGATE_INTERACTION}.countWebsiteDistinctByHCP`]) ?? 0) / hcpCount,
          type: toStringOrNull(row[`${CUBE_AGGREGATE_INTERACTION}.activity_type`], ''),
        };
      case Channel.EVENT:
        return {
          month: toDate(row[`${CUBE_AGGREGATE_INTERACTION}.interaction_date.month`]),
          rate1: (toNumberOrNull(row[`${CUBE_AGGREGATE_INTERACTION}.numberOfUniqueParticipants`]) ?? 0) / hcpCount,
          type: toStringOrNull(row[`${CUBE_AGGREGATE_INTERACTION}.activity_type`], ''),
        };
    }
  };

export const channelQueryConfig: ChannelConfigMap = {
  [Channel.EVENT]: {
    measures: [`${CUBE_AGGREGATE_INTERACTION}.numberOfInvitations`],
    dimensions: [`${CUBE_AGGREGATE_INTERACTION}.activity_type`],
    timeDimension: `${CUBE_AGGREGATE_INTERACTION}.interaction_date`,
  },
  [Channel.VISITS_1_TO_1]: {
    measures: [`${CUBE_AGGREGATE_INTERACTION}.countDistinctById`],
    dimensions: [`${CUBE_AGGREGATE_INTERACTION}.activity_type`],
    timeDimension: `${CUBE_AGGREGATE_INTERACTION}.interaction_date`,
  },
  [Channel.EMAIL]: {
    measures: [`${CUBE_AGGREGATE_INTERACTION}.countDistinctById`],
    dimensions: [`${CUBE_AGGREGATE_INTERACTION}.activity_type`],
    timeDimension: `${CUBE_AGGREGATE_INTERACTION}.interaction_date`,
  },
  [Channel.WEBSITE]: {
    measures: [`${CUBE_WEBSITE_AGGREGATE_INTERACTION}.CountUniquePageURL`],
    timeDimension: `${CUBE_WEBSITE_AGGREGATE_INTERACTION}.interaction_date`,
  },
  default: {
    measures: [`${CUBE_NAME}.countDistinctById`],
    dimensions: [`${CUBE_NAME}.type`],
    timeDimension: `${CUBE_NAME}.date`,
  },
};

export const getAdditionalFiltersByChannel = (channel: Channel): LogicalAndFilter => {
  const filter: LogicalAndFilter = { and: [] };

  if (channel === Channel.EMAIL) {
    filter.and.push({ member: `${CUBE_AGGREGATE_INTERACTION}.email_open_date`, operator: 'set' });
  }

  return filter;
};

export const getRowMatcher = (type: string, month: Dayjs, channel: Channel) => (innerRow: CubeRawRow) => {
  const interactionDateField =
    channel === Channel.WEBSITE
      ? `${CUBE_WEBSITE_AGGREGATE_INTERACTION}.interaction_date.month`
      : `${CUBE_AGGREGATE_INTERACTION}.interaction_date.month`;
  const interactionDate = innerRow[interactionDateField];

  return (
    toStringOrNull(innerRow[`${CUBE_AGGREGATE_INTERACTION}.activity_type`]) === type &&
    !!interactionDate &&
    toDate(interactionDate)?.isSame(month)
  );
};

export const getCount = (channel: Channel, row: CubeRawRow): number => {
  const channelMap: { [key in Channel]: string } = {
    [Channel.EVENT]: `${CUBE_AGGREGATE_INTERACTION}.numberOfInvitations`,
    [Channel.VISITS_1_TO_1]: `${CUBE_AGGREGATE_INTERACTION}.countDistinctById`,
    [Channel.EMAIL]: `${CUBE_AGGREGATE_INTERACTION}.countDistinctById`,
    [Channel.WEBSITE]: `${CUBE_WEBSITE_AGGREGATE_INTERACTION}.CountUniquePageURL`,
  };

  const member = channelMap[channel];
  return toNumberOrNull(row[member]) ?? 0;
};

export function createHcoPkFilter(hcoPk: string): Filter {
  return {
    member: `${HCP_CUBE_NAME}.hco_pk`,
    operator: 'equals',
    values: [hcoPk],
  };
}
